import React, { useState } from "react";
import { X, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { validateCampaign } from "../apis/campaign";
import { toast } from "react-toastify";

/**
 * Validate Campaign Modal
 * Validates a campaign and shows validation results
 */
const ValidateCampaignModal = ({ isOpen, onClose, campaign }) => {
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const handleValidate = async () => {
    try {
      setLoading(true);
      setValidationResult(null);
      const response = await validateCampaign(campaign.campaign_id);

      if (response.data) {
        setValidationResult(response.data.data || response.data);
      }
    } catch (error) {
      console.error("Error validating campaign:", error);
      toast.error(error.response?.data?.error || "Failed to validate campaign");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Validate Campaign</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-4">
              Validate the campaign <strong>{campaign.campaign_name}</strong> to check if all
              required data is valid.
            </p>

            <button
              onClick={handleValidate}
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Validating...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Validate Campaign
                </>
              )}
            </button>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className="mt-4">
              <div
                className={`p-4 rounded-lg border-2 ${
                  validationResult.valid
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {validationResult.valid ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <h4
                    className={`text-sm font-semibold ${
                      validationResult.valid ? "text-green-900" : "text-red-900"
                    }`}
                  >
                    {validationResult.message}
                  </h4>
                </div>

                {/* Errors */}
                {validationResult.errors && validationResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-red-900 mb-2">Errors:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {validationResult.errors.map((error, index) => (
                        <li key={index} className="text-xs text-red-800">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {validationResult.warnings && validationResult.warnings.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-yellow-900 mb-2">Warnings:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {validationResult.warnings.map((warning, index) => (
                        <li key={index} className="text-xs text-yellow-800">
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Campaign Info */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <strong>Campaign ID:</strong> {validationResult.campaignId}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    <strong>Status:</strong> {validationResult.status}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidateCampaignModal;

